import {useIntl} from 'react-intl';
import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import useSWR, {mutate} from "swr";
import {InvoiceSubtotals, InvoiceWithPayment, UnprocessedBankTransactionResponse} from "../../../../lib/types/types";
import {ReactiveTable} from "react-ive-tables";
import {LazyFetchingService} from "../../../../lib/services/lazy-fetching-service";
import moment from "moment/moment";
import {FilterMatchMode} from "primereact/api";
import {ColumnBodyOptions} from "primereact/column";
import {Button} from "primereact/button";
import {formatNumber, statusClassNameMap} from "../../../../lib/common";
import {InvoiceType} from "../../../invoices/Invoices";
import {IDropdownOption} from "@xal3xfx/react-utils/dist/util-service";
import {MultiSelect} from "primereact/multiselect";
import {BankTransactionService} from "../../../../lib/services/bank-transaction-service";
import {useToast} from "../../../../lib/hooks/useToast";
import {OverlayPanel} from "primereact/overlaypanel";
import {InputNumber} from "primereact/inputnumber";
import {FloatLabel} from "primereact/floatlabel";
import TransactionDetailsRow from "../../TransactionDetailsRow";
import PenaltyPaymentForInvoice from "../../../invoices/PenaltyPaymentForInvoice";

interface Props {
    transactionId?: number;
    onClose(): void;
}

const lazyFetchingService = new LazyFetchingService<InvoiceWithPayment, InvoiceSubtotals>('/invoices/lazy');


const CreatePayments: React.FC<Props> = props => {
    const {formatMessage: f} = useIntl();
    const didMountRef = useRef(false);
    const toastHelpers = useToast();
    const paymentOverlay = useRef<OverlayPanel | null>(null);


    const selectedTransactionMutationKey = `/bank/transactions/${props.transactionId}`;
    const {data: selectedTransaction} = useSWR<UnprocessedBankTransactionResponse>(props.transactionId ? selectedTransactionMutationKey : null);

    const {data: statusOptions} = useSWR<IDropdownOption[], Error>('/invoices/statuses');

    const invoiceNumberOptionsMutationKey = `/invoices/invoiceNumberDropdown/${selectedTransaction?.clientId}/1,2`;
    const {data: invoiceNumberOptions} = useSWR<IDropdownOption[], Error>(selectedTransaction ? invoiceNumberOptionsMutationKey : null);

    const bankTransactionService = new BankTransactionService(f, toastHelpers);

    const [invoicesInitialFilters, setInvoicesInitialFilters] = useState({});
    const [paymentAmount, setPaymentAmount] = useState<number | null>(null);
    const [selectedInvoiceIdForPayment, setSelectedInvoiceIdForPayment] = useState<number>();
    const [selectedInvoice, setSelectedInvoice] = useState<InvoiceWithPayment>();

    const getInitialFilters = () => {
        const initialFilters: any = {statusId: "1,2", invoiceType: "1,2,4,5"};

        if (selectedTransaction?.clientId) {
            initialFilters.clientId = String(selectedTransaction.clientId);
        }

        return initialFilters;
    }

    const fetcher = lazyFetchingService.getLazyFetcherWithInitialFilters(getInitialFilters());
    const fetchData = lazyFetchingService.getDataFetcher;


    const invoicesMutationKey = "/invoices/lazy";
    const {data: invoices} = useSWR<Awaited<ReturnType<typeof fetcher>>, Error>(selectedTransaction ? invoicesMutationKey : null, {fetcher: fetcher});


    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
        }

        return () => {
            mutate(invoicesMutationKey);
        }
    }, []);

    useEffect(() => {
        if(selectedTransaction && selectedTransaction.remainingAmount === 0) {
            props.onClose();
        }
    }, [selectedTransaction])


    const generateStatusColumnTemplate = (rowData: InvoiceWithPayment) => {
        const statusName = statusOptions?.find(status => status.id === rowData.statusId)?.description || "";
        const className = statusClassNameMap[statusName];
        return <div key={rowData.id} style={{display: 'flex', justifyContent: 'center'}}><span
            style={{display: 'flex', justifyContent: 'center'}}
            className={className}>{f({id: statusName})}</span></div>;
    }

    const getPenaltyTemplate = (rowData: InvoiceWithPayment) => {
        if (rowData.penalty === 0)
            return formatNumber(rowData.penalty);
        else
            return <Button label={formatNumber(rowData.penalty).toString()} onClick={() => {
                setSelectedInvoice(rowData);
                // setShowInvoicePenaltiesDialog(true);
            }}/>

    }

    const getInvoicesColumnTemplate = () => {
        return {
            operations: (rowData: InvoiceWithPayment, columnOptions: ColumnBodyOptions) => {

                return <div className={'flex justify-content-center gap-1'}>
                    <Button icon={'pi pi-plus'} className={'mr-2 p-button-success'} tooltip={f({id: 'addPayment'})}
                            disabled={rowData.penalty > 0}
                            tooltipOptions={{position: 'top'}} onClick={(e) => {
                        setPaymentAmount(Math.min(selectedTransaction?.remainingAmount || 0, rowData.restAmount));
                        setSelectedInvoiceIdForPayment(rowData.id);
                        paymentOverlay.current?.toggle(e);
                    }}/>

                </div>
            },
            date: (rowData: InvoiceWithPayment) => moment(rowData.date).format("DD-MM-YYYY"),
            total: (rowData: InvoiceWithPayment) => formatNumber(rowData.total),
            paidAmount: (rowData: InvoiceWithPayment) => formatNumber(rowData.paidAmount),
            restAmount: (rowData: InvoiceWithPayment) => formatNumber(rowData.restAmount),
            penalty: getPenaltyTemplate,
            statusId: generateStatusColumnTemplate,
            dueDate: (rowData: InvoiceWithPayment) => moment(rowData.dueDate).format("DD-MM-YYYY"),
            invoiceType: (rowData: InvoiceWithPayment) => {
                let invoiceTypeLabel = "";
                let severity: "success" | "info" | "help" | "danger" | "secondary" | "warning" = "info";
                let tooltip = "";

                switch (rowData.invoiceType) {
                    case InvoiceType.Invoice: {
                        invoiceTypeLabel = "Ф";
                        severity = "info";
                        tooltip = f({id: 'invoice'});
                        break;
                    }
                    case InvoiceType.PenaltyInvoice: {
                        invoiceTypeLabel = "ФН";
                        severity = "help";
                        tooltip = f({id: 'penaltyInvoice'});
                        break;
                    }
                    case InvoiceType.CreditNotice: {
                        invoiceTypeLabel = "КИ";
                        severity = "danger";
                        tooltip = f({id: 'creditNote'});
                        break;
                    }
                    case InvoiceType.TransactionsInvoice: {
                        invoiceTypeLabel = "Т";
                        severity = "success";
                        tooltip = f({id: 'transactions'});
                        break;
                    }
                    case InvoiceType.ManualInvoice: {
                        invoiceTypeLabel = "РФ";
                        severity = "success";
                        tooltip = f({id: 'manualInvoice'});
                        break;
                    }
                    case InvoiceType.AdvancePayment: {
                        invoiceTypeLabel = "АП";
                        severity = "success";
                        tooltip = f({id: 'advancePayment'});
                        break;
                    }
                }

                return <>
                    <Button
                        tooltip={tooltip}
                        tooltipOptions={{position: "top"}}
                        label={invoiceTypeLabel}
                        severity={severity}
                    />
                </>


            }
        }
    }

    const getSpecialFilters = () => {
        return {
            invoiceNumber: (options: any) => <MultiSelect filter={true} showClear value={options.value}
                                                          maxSelectedLabels={2}
                                                          resetFilterOnHide
                                                          virtualScrollerOptions={{itemSize: 30}}
                                                          options={invoiceNumberOptions || []}
                                                          optionValue={'description'} optionLabel={'description'}
                                                          placeholder={f({id: 'chooseLabel'})}
                                                          onChange={(e) => options.filterApplyCallback(e.value)}
                                                          style={{textAlign: "left"}}/>,
        }
    }

    const handleCreatePayment = async () => {
        if (!selectedTransaction?.id || !selectedInvoiceIdForPayment || !paymentAmount || paymentAmount <= 0) return;
        const result = await bankTransactionService.createPayment(selectedTransaction?.id, selectedInvoiceIdForPayment, paymentAmount)

        if (result) {
            await mutate(selectedTransactionMutationKey);
            await mutate(invoiceNumberOptionsMutationKey);
            await mutate(invoicesMutationKey);
            paymentOverlay.current?.hide()
        }
    }

    const rowClass = (data: InvoiceWithPayment) => {
        return {
            'overdue-invoice': data.statusId < 3 && new Date() > new Date(data.dueDate)
        }
    }

    const closeInvoicePenaltyDialog = async () => {
        setSelectedInvoice(undefined);
        await mutate(selectedTransactionMutationKey);
        await mutate(invoiceNumberOptionsMutationKey);
        await mutate(invoicesMutationKey);
    }

    return <>
        {selectedTransaction &&
           <TransactionDetailsRow selectedTransaction={selectedTransaction} />
        }

        <div className={"mt-4"}>
            <h2 className={"font-bold"}>{f({id: "invoices"})}</h2>
            <ReactiveTable
                swr
                data={invoices?.rows}
                totalRecords={invoices?.totalRecords}
                fetchData={fetchData}
                columnOrder={['invoiceNumber', 'date', 'total', 'paidAmount', 'restAmount', 'penalty', 'dueDate', 'statusId', 'invoiceType', 'operations']}
                ignoreFilters={['date', 'total', 'paidAmount', 'restAmount', 'penalty', 'dueDate', 'statusId', 'invoiceType', 'operations']}
                showFilters={true}
                columnTemplate={getInvoicesColumnTemplate()}
                specialFilters={getSpecialFilters()}
                xlsx={`${moment().format("MM-DD HH-mm")}_${f({id: 'invoices'})}`}
                initialFilters={invoicesInitialFilters}
                filtersMatchMode={{statusId: FilterMatchMode.EQUALS, invoiceNumber: FilterMatchMode.IN}}
                specialLabels={{date: 'invoiceDate', total: 'vatIncluded', statusId: 'status'}}
                columnStyle={{
                    operations: {
                        header: {},
                        body: {width: '1px'}
                    }
                }}
                dtProps={{
                    rowClassName: rowClass
                }}
            />

            <OverlayPanel ref={paymentOverlay} showCloseIcon>
                <div className={"flex flex-column gap-2"}>
                    <h2>{f({id: 'createPayment'})}</h2>
                    <FloatLabel className={"mt-4"}>
                        <InputNumber value={paymentAmount} onChange={(e) => setPaymentAmount(e.value)}/>

                        <label htmlFor="username">{f({id: "amount"})}</label>
                    </FloatLabel>
                    <Button label={f({id: "save"})} disabled={paymentAmount === null || paymentAmount <= 0} onClick={handleCreatePayment}/>
                </div>
            </OverlayPanel>

        </div>
        {(selectedInvoice && selectedTransaction) &&
            <PenaltyPaymentForInvoice selectedInvoice={selectedInvoice} closeDialog={closeInvoicePenaltyDialog}
                                      previewOnly={false} bankTransaction={selectedTransaction} />}
    </>
};

export default CreatePayments
